import { useState, useEffect, useRef } from 'react';
import { apiClient } from '@services/api';
import Button from '@components/Button';
import Icon from '@material-ui/core/Icon';
import CircularProgress from '@material-ui/core/CircularProgress';
import TextField from '@components/TextField';

const EMPTY_FORM = { code: '', discount_percent: '', expires_at: '', tour_id: '', tour_name: '' };

// Estilos para inputs legíveis no modal claro
const LIGHT_INPUT = {
  InputProps:      { style: { backgroundColor: '#fff', color: '#172B4D' } },
  InputLabelProps: { style: { color: '#64748b' } },
};

// ─── Componente de busca de passeio ────────────────────────────────────────────
const TourSearchInput = ({ label, onSelect }) => {
  const [query,   setQuery]   = useState('');
  const [results, setResults] = useState([]);
  const [open,    setOpen]    = useState(false);
  const [busy,    setBusy]    = useState(false);
  const timer = useRef(null);

  const search = (q) => {
    if (!q || q.length < 2) { setResults([]); setOpen(false); return; }
    setBusy(true);
    apiClient.httpInstance
      .get('/tours', { params: { q, per_page: 10 } })
      .then(res => {
        const data = res.data?.data || res.data || [];
        setResults(Array.isArray(data) ? data.slice(0, 10) : []);
        setOpen(true);
      })
      .catch(() => setResults([]))
      .finally(() => setBusy(false));
  };

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    if (!val) onSelect({ id: null, name: '' });
    clearTimeout(timer.current);
    timer.current = setTimeout(() => search(val), 350);
  };

  const handleSelect = (tour) => {
    setQuery(tour.name);
    setOpen(false);
    onSelect(tour);
  };

  const handleClear = () => {
    setQuery('');
    setResults([]);
    setOpen(false);
    onSelect({ id: null, name: '' });
  };

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <TextField
        className="inputRounded"
        label={label}
        variant="outlined"
        value={query}
        onChange={handleChange}
        onFocus={() => results.length && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        InputProps={{
          style: { backgroundColor: '#fff', color: '#172B4D' },
          endAdornment: busy
            ? <CircularProgress size={16} style={{ color: '#94a3b8' }} />
            : query
              ? (
                <button
                  type="button"
                  onClick={handleClear}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 0, lineHeight: 1 }}
                >
                  <Icon style={{ fontSize: 16 }}>close</Icon>
                </button>
              ) : null,
        }}
        InputLabelProps={{ style: { color: '#64748b' } }}
      />
      {open && results.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0,
          background: '#fff', borderRadius: 8,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          zIndex: 1000, maxHeight: 220, overflowY: 'auto',
          border: '1px solid #e2e8f0',
        }}>
          {results.map(t => (
            <div
              key={t.id}
              onMouseDown={() => handleSelect(t)}
              style={{ padding: '10px 14px', cursor: 'pointer', color: '#172B4D', fontSize: 14 }}
              onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}
            >
              {t.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Componente principal ──────────────────────────────────────────────────────
const PromoAppIndex = (props) => {
  const [promos, setPromos]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [saving, setSaving]       = useState(false);
  const [errors, setErrors]       = useState({});
  const [toggling, setToggling]   = useState(null);
  const [deleting, setDeleting]   = useState(null);

  useEffect(() => { props.setShowMenu(false); }, []);

  const fetchPromos = () => {
    setLoading(true);
    apiClient.httpInstance
      .get('/app-promo-codes')
      .then(res => setPromos(res.data.data || []))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchPromos(); }, []);

  const change = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const openModal = () => {
    setForm(EMPTY_FORM);
    setErrors({});
    setShowModal(true);
  };

  const handleCreate = (e) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});
    apiClient.httpInstance
      .post('/app-promo-codes', form)
      .then(() => { setShowModal(false); setForm(EMPTY_FORM); fetchPromos(); })
      .catch(err => { if (err.response?.data?.errors) setErrors(err.response.data.errors); })
      .finally(() => setSaving(false));
  };

  const handleToggle = (promo) => {
    setToggling(promo.id);
    apiClient.httpInstance
      .put(`/app-promo-codes/${promo.id}`, { active: !promo.active })
      .then(res => setPromos(ps => ps.map(p => p.id === promo.id ? res.data.data : p)))
      .finally(() => setToggling(null));
  };

  const handleDelete = (id) => {
    if (!window.confirm('Excluir este cupom?')) return;
    setDeleting(id);
    apiClient.httpInstance
      .delete(`/app-promo-codes/${id}`)
      .then(() => setPromos(ps => ps.filter(p => p.id !== id)))
      .finally(() => setDeleting(null));
  };

  const isExpired = (expires_at) =>
    expires_at && new Date(expires_at) < new Date();

  return (
    <div className="px-7 pb-10">
      {/* Header */}
      <div className="flex items-center justify-between mt-2 text-2xl" style={{ color: props.theme.color }}>
        <span>Promoções FT App</span>
        <Button
          textcolor="#fff"
          padding="6px 15px"
          margintop="0px"
          width="180"
          onClick={openModal}
          customcolor={props.theme.color}
        >
          <Icon className="mr-2" style={{ fontSize: 18 }}>add</Icon> Nova promoção
        </Button>
      </div>

      <p className="mt-2 text-sm" style={{ color: '#64748b' }}>
        Cupons exclusivos para usuários do FT App. Aparecem automaticamente na tela de passeios para clientes elegíveis com alta intenção de compra.
      </p>

      {/* Lista */}
      <div className="mt-5 space-y-3">
        {loading ? (
          <div className="flex justify-center mt-16">
            <CircularProgress style={{ color: props.theme.color }} />
          </div>
        ) : promos.length === 0 ? (
          <div className="bg-white rounded-xl p-10 text-center" style={{ color: '#64748b' }}>
            <Icon style={{ fontSize: 40, display: 'block', margin: '0 auto 8px' }}>local_offer</Icon>
            Nenhuma promoção criada ainda.
          </div>
        ) : promos.map(promo => {
          const expired = isExpired(promo.expires_at);
          const statusColor = !promo.active ? '#64748b' : expired ? '#ef4444' : '#22c55e';
          const statusLabel = !promo.active ? 'Inativo' : expired ? 'Expirado' : 'Ativo';
          return (
            <div
              key={promo.id}
              className="bg-white rounded-xl p-4 flex items-center gap-4"
              style={{ border: '1px solid #e2e8f0' }}
            >
              {/* Código badge */}
              <span style={{
                background: props.theme.color + '18',
                color: props.theme.color,
                fontWeight: 700, fontSize: 15, borderRadius: 8,
                padding: '4px 14px', letterSpacing: 1,
                minWidth: 100, textAlign: 'center', whiteSpace: 'nowrap',
              }}>
                {promo.code}
              </span>

              {/* Desconto */}
              <span className="text-blue font-bold text-lg" style={{ minWidth: 52 }}>
                {promo.discount_percent}%
              </span>

              {/* Tour / Todos */}
              <div className="flex-1">
                <div className="text-sm font-medium" style={{ color: '#172B4D' }}>
                  {promo.tour_name || <span style={{ color: '#94a3b8' }}>Todos os passeios</span>}
                </div>
                {promo.expires_at && (
                  <div className="text-xs mt-0.5" style={{ color: expired ? '#ef4444' : '#64748b' }}>
                    Válido até {new Date(promo.expires_at).toLocaleDateString('pt-BR')}
                  </div>
                )}
              </div>

              {/* Status badge */}
              <span style={{
                background: statusColor + '18', color: statusColor,
                fontWeight: 600, fontSize: 12, borderRadius: 6, padding: '2px 10px',
              }}>
                {statusLabel}
              </span>

              {/* Toggle ativo */}
              <button
                onClick={() => handleToggle(promo)}
                disabled={toggling === promo.id}
                title={promo.active ? 'Desativar' : 'Ativar'}
                style={{ background: 'none', border: 'none', cursor: 'pointer',
                  color: promo.active ? '#22c55e' : '#94a3b8', padding: 4 }}
              >
                {toggling === promo.id
                  ? <CircularProgress size={18} style={{ color: props.theme.color }} />
                  : <Icon>{promo.active ? 'toggle_on' : 'toggle_off'}</Icon>
                }
              </button>

              {/* Deletar */}
              <button
                onClick={() => handleDelete(promo.id)}
                disabled={deleting === promo.id}
                title="Excluir"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 4 }}
              >
                {deleting === promo.id
                  ? <CircularProgress size={18} style={{ color: '#ef4444' }} />
                  : <Icon>delete_outline</Icon>
                }
              </button>
            </div>
          );
        })}
      </div>

      {/* Modal de criação */}
      {showModal && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(0,0,0,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={() => setShowModal(false)}
        >
          <div
            style={{ background: '#fff', borderRadius: 16, padding: 28, width: 440, maxWidth: '90vw' }}
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-5">
              <span className="text-xl text-blue font-semibold">Nova promoção</span>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <Icon style={{ color: '#94a3b8' }}>close</Icon>
              </button>
            </div>

            <form onSubmit={handleCreate} className="space-y-4">
              <TextField
                className="inputRounded"
                label="Código do cupom"
                name="code"
                variant="outlined"
                value={form.code}
                onChange={change}
                error={errors.code}
                helperText={errors.code?.[0]}
                required
                inputProps={{ style: { textTransform: 'uppercase', color: '#172B4D' } }}
                {...LIGHT_INPUT}
              />
              <TextField
                className="inputRounded"
                label="% de desconto"
                name="discount_percent"
                type="number"
                variant="outlined"
                value={form.discount_percent}
                onChange={change}
                error={errors.discount_percent}
                helperText={errors.discount_percent?.[0]}
                required
                {...LIGHT_INPUT}
              />
              <TextField
                className="inputRounded"
                label="Válido até (opcional)"
                name="expires_at"
                type="date"
                variant="outlined"
                value={form.expires_at}
                onChange={change}
                InputLabelProps={{ shrink: true, style: { color: '#64748b' } }}
                InputProps={{ style: { backgroundColor: '#fff', color: '#172B4D' } }}
              />

              {/* Busca de passeio com autocomplete */}
              <TourSearchInput
                label="Passeio específico (opcional — deixe vazio para todos)"
                onSelect={tour => setForm(f => ({
                  ...f,
                  tour_id:   tour.id   ? String(tour.id) : '',
                  tour_name: tour.name || '',
                }))}
              />
              {form.tour_name && (
                <p className="text-xs" style={{ color: '#1a56db', marginTop: -12 }}>
                  ✓ {form.tour_name}
                </p>
              )}

              <div className="flex justify-end gap-3 pt-2">
                <Button
                  textcolor="#64748b"
                  padding="6px 15px"
                  width="120"
                  variant="outlined"
                  bgColor="transparent"
                  onClick={() => setShowModal(false)}
                  type="button"
                >
                  Cancelar
                </Button>
                <Button
                  textcolor="#fff"
                  padding="6px 15px"
                  width="160"
                  customcolor={props.theme.color}
                  type="submit"
                  disabled={saving}
                >
                  {saving ? <CircularProgress size={18} style={{ color: '#fff' }} /> : 'Criar promoção'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromoAppIndex;
